import { useSidepanel } from '@renderer/overlay/atoms'
import {
  useSidepanelFloatingBarHeight,
  useGetSidepanelScrollPosition,
  useSetSidepanelScrollPosition,
} from '@renderer/overlay/Sidepanel/atoms'
import { useGlobalState } from '@highlight/ipc/react'
import { ComponentProps, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { setHomeScrolling } from '../../../hooks/sidepanel-scroll-state'

interface SidepanelBodyProps extends ComponentProps<'div'> {
  /**
   * A drill-in View is on top, so Home is unmounted and this body is empty
   * (ADR 0001). Drives the scroll bookkeeping below and Home's fade back in.
   */
  isViewOpen?: boolean
  /**
   * Freeze the scroller (quick tour). Must be set here rather than via
   * `className`: the className lands on the outer wrapper, while the element
   * that actually scrolls is the inner container below. Programmatic
   * `scrollTop` still works, so the tour can keep bringing sections into view.
   */
  scrollLocked?: boolean
}

export function SidepanelBody({ children, className, style, isViewOpen, scrollLocked, ...props }: SidepanelBodyProps) {
  const [sidepanelState, setSidepanelState] = useSidepanel()
  const floatingBarHeight = useSidepanelFloatingBarHeight()
  const getScrollPosition = useGetSidepanelScrollPosition()
  const setScrollPosition = useSetSidepanelScrollPosition()
  const [isSwiping] = useGlobalState<boolean>('sidepanel.isSwiping', false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isRestoringScroll = useRef(false)
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout>()
  // Live scroll offset kept in a ref so onScroll never triggers a React render.
  const liveScrollTopRef = useRef(0)

  // While a drill-in View is open the body chrome stays mounted but its
  // content (the Home sections) is unmounted by the view router, so the
  // scroll container collapses and clamps to 0. Mirrored in a ref so the
  // scroll handler can ignore those clamp events.
  const isViewOpenRef = useRef(!!isViewOpen)
  isViewOpenRef.current = !!isViewOpen

  const handleChildrenClick = useCallback(() => {
    if (sidepanelState === 'peeking') {
      setSidepanelState('open')
    }
  }, [sidepanelState, setSidepanelState])

  // Restore scroll position on mount and whenever Home's sections remount
  // after a drill-in View closes (useLayoutEffect = before paint). Read
  // imperatively so the body never subscribes to the position atom.
  useLayoutEffect(() => {
    if (isViewOpen) {
      return
    }
    const saved = getScrollPosition()
    if (containerRef.current && saved > 0) {
      isRestoringScroll.current = true
      containerRef.current.scrollTop = saved
      liveScrollTopRef.current = saved
      // Reset flag after restoration completes
      requestAnimationFrame(() => {
        isRestoringScroll.current = false
      })
    }
  }, [getScrollPosition, isViewOpen])

  // Track scroll changes and active scrolling state. The live offset is stored
  // in a ref (no render); the atom is only written on scroll-end so restoration
  // works without re-rendering the body and every section on each scroll frame.
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (isRestoringScroll.current) return // Ignore scroll during restoration
      if (isViewOpenRef.current) {
        return // Ignore the clamp-to-0 while sections are unmounted
      }
      liveScrollTopRef.current = e.currentTarget.scrollTop

      // Mark as actively scrolling (prevents swipe gesture)
      if (!isScrollingRef.current) {
        isScrollingRef.current = true
        setHomeScrolling(true)
      }

      // Reset scrolling state and persist the final position after scroll stops
      clearTimeout(scrollTimeoutRef.current)
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false
        setHomeScrolling(false)
        setScrollPosition(liveScrollTopRef.current)
      }, 150)
    },
    [setScrollPosition],
  )

  // Cleanup on unmount: clear timeout and persist the final scroll position so
  // it can be restored when the body remounts (e.g. sidepanel reopens).
  useEffect(() => {
    return () => {
      clearTimeout(scrollTimeoutRef.current)
      setHomeScrolling(false)
      setScrollPosition(liveScrollTopRef.current)
    }
  }, [setScrollPosition])

  const marginPixels = 12
  const scrollPaddingBottom = floatingBarHeight + marginPixels

  // Home's fade back in. Held at half opacity while a View owns the panel —
  // invisible then, since the body is empty — so that when the View pops, the
  // remounting sections rise to full opacity across the View's own dissolve
  // rather than snapping in behind it.
  const opacity = isViewOpen ? 0.5 : 1

  // No top padding: the first child owns its own top spacing (SidepanelSection's
  // `py-5`), so padding here stacked on top of it and left the first section's
  // header sitting twice as far below the header hairline as every other one.
  const scrollerStyle = {
    paddingBottom: scrollPaddingBottom,
    opacity,
    transition: 'opacity 0.2s ease-out',
    // Disable scrolling during swipe gesture to prevent conflicting interactions
    ...((isSwiping || scrollLocked) && { overflowY: 'hidden' as const }),
    ...style,
  }

  return (
    <div
      onClick={handleChildrenClick}
      className={twMerge(
        'pointer-events-auto mb-0 flex min-h-0 flex-1 flex-col',
        sidepanelState === 'peeking' && 'cursor-pointer',
        className,
      )}
      {...props}
    >
      {/* Scroll clip. The unified surface's own overflow/radius cuts content at
          the panel silhouette; this box just contains the scroll area. */}
      <div className="h-full overflow-hidden">
        {/* layoutScroll lets framer's reorder layout animations account for this
            container's scroll offset, so dragging a section to reorder lands
            correctly even when the body is scrolled. */}
        <motion.div
          layoutScroll
          ref={containerRef}
          onScroll={handleScroll}
          className="invisible-scrollbar flex h-full flex-col overflow-y-auto [overflow-anchor:none]"
          style={scrollerStyle}
        >
          {/* grow lets a short child (the sign-in gate) center itself in the
              viewport; sections still stack from the top as before. */}
          <div className="relative flex grow flex-col">{children}</div>
        </motion.div>
      </div>
    </div>
  )
}
